import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"

import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined"
import AddOutlinedIcon from "@mui/icons-material/AddOutlined"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { Card, CardContent, CardHeader, Grid, IconButton } from "@mui/material"
import { Button, InputText, Menu, MenuItem, Tooltip, Typography } from "@synapse-analytics/synapse-ui"
import { FieldArray, useFormikContext } from "formik"

import { BaseSimpleDialog } from "../../../components/dialogs/BaseSimpleDialog"
import { getTheme } from "../../../hooks/UseTheme"
import type { Rule } from "../../../types/custom/rules"
import { getEnvVar } from "../../../utils/genericHelpers"
import { handleRemoveCondition } from "../../Ruleset"
import { Condition } from "../../Ruleset/components/Condition"
import { createEmptyCondition, createNewGroup } from "../helpers"
import type { TagCardFormikValues, TagCardProps, TagGroupConditions } from "../interface"
import { TagGroupRenderer } from "./TagGroupRenderer"

import styles from "../../Ruleset/components/RuleCard.module.scss"

/**
 * TagCard component for displaying and editing individual tag rules within a taglist
 *
 * This component renders a single tag rule card that can be in edit, create, or view mode.
 * It supports nested condition groups up to 3 levels deep and provides functionality
 * to add, remove, and modify conditions within each group.
 *
 * @param {TagCardProps} props - Component props
 * @param {boolean} [props.editMode=false] - Whether the component is in edit mode
 * @param {boolean} [props.createMode=false] - Whether the component is in create mode
 * @param {number} props.index - Index of this card within the parent taglist
 * @param {["and", "or"]} props.andOr - Available logical operators for conditions
 * @param {function} [props.handleDelete] - Function to handle tag deletion
 * @param {function} [props.handleRemoveRule] - Function to handle rule removal
 * @returns {React.ReactElement} The rendered TagCard component
 */
export function TagCard(props: Readonly<TagCardProps>): React.ReactElement {
  // Destructure props with default values
  const { editMode = false, createMode = false, handleDelete, handleRemoveRule, index, andOr } = props

  // Hooks
  const { t } = useTranslation("taglists")
  const { values, setFieldValue, handleChange, handleBlur, setValues, isSubmitting, errors, touched } =
    useFormikContext<TagCardFormikValues>()
  const theme = getTheme()

  // State management
  const [openRuleDeletionDialog, setOpenRuleDeletionDialog] = useState<boolean>(false)
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

  // Constants
  const isMenuOpen = Boolean(menuAnchor)
  const openRuleCardMenu = Boolean(anchor)
  const emptyCondition = createEmptyCondition()
  const MAX_GROUP_DEPTH = Number(getEnvVar("RULESETS_TAGLISTS_GROUPS_DEPTH", 3))
  return (
    <Card className={styles.card}>
      {openRuleDeletionDialog && (
        <BaseSimpleDialog
          open={openRuleDeletionDialog}
          name={values?.cards[index]?.name as string}
          onClose={() => setOpenRuleDeletionDialog(false)}
          onAccept={() => {
            if (handleDelete) {
              handleDelete(values?.cards[index]?.name as string, setOpenRuleDeletionDialog)
            }
          }}
          mode={"rule-deletion"}
          isLoading={isSubmitting}
        />
      )}

      <CardHeader
        className={styles.cardHeader}
        title={
          <Grid container justifyContent="flex-start" alignItems="center" spacing={2}>
            <Grid item xs={6}>
              {editMode || createMode ? (
                <InputText
                  hideDescription
                  id={`cards[${index}].name`}
                  placeholder={t("TagCard.labels.name")}
                  value={values.cards[index]?.name}
                  handleChange={handleChange}
                  error={
                    touched?.cards &&
                    errors?.cards &&
                    touched?.cards[index]?.name &&
                    Boolean((errors?.cards[index] as TagGroupConditions)?.name) &&
                    (errors?.cards[index] as TagGroupConditions)?.name
                  }
                  handleBlur={handleBlur}
                  disabled={isSubmitting}
                  description={t("TagCard.descriptions.card")}
                  fullWidth
                />
              ) : (
                <Typography variant="h3-bold" style={{ textTransform: "inherit" }}>
                  {values.cards[index]?.name}
                </Typography>
              )}
            </Grid>
          </Grid>
        }
        action={
          handleDelete &&
          handleRemoveRule && (
            <Fragment>
              <IconButton
                aria-label="settings"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => setAnchor(e.currentTarget)}
              >
                <MoreHorizIcon htmlColor={theme.palette.base.text[1]} />
              </IconButton>
              <Menu
                key="basic-menu"
                anchorEl={anchor}
                open={openRuleCardMenu}
                onClose={() => setAnchor(null)}
                menuMaxContent
              >
                <Tooltip
                  width={"100%"}
                  title={1 === values?.cards?.length && t("TagCard.tooltips.cannotRemoveLastTag")}
                >
                  <MenuItem
                    onClick={() => {
                      const removeIndicator = handleRemoveRule?.(index)
                      if (removeIndicator) {
                        setOpenRuleDeletionDialog(true)
                      }
                      setAnchor(null)
                    }}
                    disabled={values?.cards?.length === 1 || isSubmitting}
                  >
                    <Typography variant="a" color="negative" variantColor={2}>
                      {t("TagCard.menu.removeTag")}
                    </Typography>
                  </MenuItem>
                </Tooltip>
              </Menu>
            </Fragment>
          )
        }
      />

      <CardContent className={styles.cardContent}>
        <FieldArray name={`cards[${index}].conditions`}>
          {/* Level 1 group */}
          {({ push: level1push, remove: level1remove }) => (
            <Grid container justifyContent="flex-end" alignItems="center" className={styles.groupBackground}>
              <Grid container item spacing={1}>
                {values?.cards[index]?.conditions?.map((item: Rule, idx: number) => (
                  <Grid item xs={12} key={`level1-${index}-${idx}-${item.id}`}>
                    <Condition
                      key={item?.id}
                      path={{ groupsIdx: [], conditionIdx: idx }}
                      id={`cards[${index}].conditions[${idx}]`}
                      index={idx}
                      editMode={editMode}
                      createMode={createMode}
                      ruleIndex={index}
                      removeCondition={() => {
                        handleRemoveCondition({
                          values,
                          setFieldValue,
                          setValues,
                          levelRemove: level1remove,
                          ruleIndex: index,
                          path: {
                            groupsIdx: [],
                            conditionIdx: idx,
                          },
                        })
                        level1remove(idx)
                      }}
                      lastSwitchSetter={(op) => {
                        setFieldValue(`cards[${index}].conditions[0].andOr`, op)
                      }}
                    />
                  </Grid>
                ))}

                {/* Recursive rendering of nested groups */}
                {values.cards[index]?.groups && values.cards[index]?.groups.length > 0 && (
                  <TagGroupRenderer
                    groups={values.cards[index].groups}
                    ruleIndex={index}
                    parentPath={[]}
                    editMode={editMode}
                    createMode={createMode}
                    currentDepth={0}
                    maxDepth={MAX_GROUP_DEPTH}
                    andOr={andOr}
                    setValues={setValues}
                  />
                )}

                {/* Level 1 Add Rule/Group Menu Button */}
                {(editMode || createMode) && (
                  <Grid container justifyContent="flex-start" alignItems="center">
                    <Grid item lg={12} mt={1} ml={1}>
                      <Button
                        size="regular"
                        onClick={(e) => setMenuAnchor(e?.currentTarget as HTMLElement)}
                        disabled={isSubmitting}
                      >
                        + {t("TagCard.buttons.addRule")}
                      </Button>
                      <Menu
                        key="basic-menu1"
                        anchorEl={menuAnchor}
                        open={isMenuOpen}
                        onClose={() => setMenuAnchor(null)}
                        menuMaxContent
                      >
                        <MenuItem
                          onClick={() => {
                            level1push({ ...emptyCondition, andOr: "and" })
                            setMenuAnchor(null)
                          }}
                        >
                          <Grid container display={"flex"} gap={1} height={"20px"}>
                            <Grid item>
                              <AddOutlinedIcon fontSize="small" htmlColor={theme.palette.base.text[1]} />
                            </Grid>
                            <Grid item>
                              <Typography variant="a">{t("TagCard.buttons.addRule")}</Typography>
                            </Grid>
                          </Grid>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            const newGroup = createNewGroup()
                            setFieldValue(`cards[${index}].groups`, [...(values.cards[index]?.groups || []), newGroup])
                            setMenuAnchor(null)
                          }}
                        >
                          <Grid container display={"flex"} gap={1} height={"20px"}>
                            <Grid item>
                              <AddBoxOutlinedIcon fontSize="small" htmlColor={theme.palette.base.text[1]} />
                            </Grid>
                            <Grid item>
                              <Typography variant="a">{t("TagCard.buttons.addGroup")}</Typography>
                            </Grid>
                          </Grid>
                        </MenuItem>
                      </Menu>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        </FieldArray>
      </CardContent>
    </Card>
  )
}
